/**
 * useAuthenticatedDatasetImageUrl
 *
 * For private dataset file URLs (e.g. /api/v1/datasets/.../files/...), the browser would
 * request them from the page origin without the API Bearer token, causing 404 (wrong host)
 * or 401 (no auth). This hook fetches the image with auth and returns a blob object URL
 * so <img src={src} /> works.
 *
 * @param {string|null|undefined} logoUrl - Relative path (e.g. /api/v1/datasets/.../files/...)
 *   or full URL. If full URL (http/https) or empty, returned as-is (no fetch).
 * @returns {{ src: string|null, loading: boolean, error: string|null }}
 */
import { useState, useEffect, useRef } from 'react';
import { getApiClient } from 'utils/apiClient';

function getApiBaseUrl() {
  if (typeof window === 'undefined') return 'http://localhost:5100';
  const env = window._env_ || {};
  if (env.API_BASE_URL) return String(env.API_BASE_URL).replace(/\/$/, '');
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5100';
  }
  return env.API_BASE_URL || '';
}

function isDatasetFilePath(url) {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return (trimmed.startsWith('/api/v1/datasets/') && trimmed.includes('/files/')) || trimmed.startsWith('http');
}

function buildFullUrl(path) {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = getApiBaseUrl();
  const pathStart = path.startsWith('/') ? path : `/${path}`;
  return `${base}${pathStart}`;
}

export function useAuthenticatedDatasetImageUrl(logoUrl) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const revokedRef = useRef(null);

  useEffect(() => {
    if (!logoUrl || typeof logoUrl !== 'string' || !logoUrl.trim()) {
      setSrc(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const trimmed = logoUrl.trim();
    if (!isDatasetFilePath(trimmed)) {
      setSrc(trimmed);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const api = getApiClient();
        const token = await api.getApiAccessToken?.();
        const fullUrl = buildFullUrl(trimmed);
        const res = await fetch(fullUrl, {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'omit',
        });
        if (cancelled) return;
        if (!res.ok) {
          setError(`Image load failed: ${res.status}`);
          setSrc(null);
          setLoading(false);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        if (revokedRef.current) {
          try { URL.revokeObjectURL(revokedRef.current); } catch (_) {}
          revokedRef.current = null;
        }
        const objectUrl = URL.createObjectURL(blob);
        revokedRef.current = objectUrl;
        setSrc(objectUrl);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Failed to load image');
          setSrc(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (revokedRef.current) {
        try { URL.revokeObjectURL(revokedRef.current); } catch (_) {}
        revokedRef.current = null;
      }
    };
  }, [logoUrl]);

  return { src, loading, error };
}
